import { Fragment, lazy, useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { UncontrolledTooltip } from 'reactstrap';
import List from 'src/componentes/list/tree';
import ListColumn from 'src/componentes/list/list-column';
import ContentManager from 'src/componentes/content-manager';
import Confirm from 'src/componentes/message-box/confirm';
import http from 'src/services/httpService';
import LucideIcon from 'src/componentes/lucide-icon';
import Button from 'src/componentes/button';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/errorHandler';
import { TIPO_INTERACAO_SOPHIE } from 'src/utils/constants';
import { SophieIcon } from 'src/componentes/icons';
import useAuth from 'src/hooks/useAuth';

const ConsequenciasRiscoEdit = lazy(() => import('./edit'));
const ConsequenciaControleEdit = lazy(() =>
  import('../../components/ConsequenciaControleEdit')
);
const ValidacaoRestricao = lazy(() =>
  import('src/paginas/item/validacao-restricao')
);
const AssistenteVirtual = lazy(() =>
  import('src/components/IA/ChatGPT/AssistenteVirtual')
);

const ConsequenciasRiscoList = (props) => {
  const intl = useIntl();
  const { terms: resources, moduloInteligenciaArtificial, user } = useAuth();

  const {
    configuracao,
    disabled,
    model,
    riscoId,
    refreshCount,
    refreshAfterRemove,
  } = props;

  const [saved, setSaved] = useState(-1);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setSaved((prev) => prev + 1);
  }, [refreshAfterRemove]);

  const handleCloseEdit = (salvo) => {
    if (salvo) {
      refreshCount && refreshCount(salvo);
      setSaved(Math.random());
    }
  };

  const handleDescricaoClick = (item) => {
    ContentManager.addContent(
      <ConsequenciaControleEdit
        defaultActiveKey={0}
        handleClose={handleCloseEdit}
        item={item}
      />
    );
  };

  const handleDesvincularClick = (item) => {
    ContentManager.addContent(
      <ValidacaoRestricao
        url="/RiscoConsequenciaControle/ValidarRestricoes"
        item={item}
        urlExclusao={'RiscoConsequencias/Remove'}
        isEdit={false}
        onClose={handleCloseEdit}
        validarAlteracao={false}
      />
    );
  };

  const handleConfirmarExclusao = (item) => {
    http
      .post('RiscoConsequenciaControle/Remove', {
        id: item.id,
      })
      .then(() => {
        handleCloseEdit(true);
        refreshCount && refreshCount(true);
      });
  };

  const handleDesvincularConfirmClick = (item) => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage(
          { id: 'desvincularControleCausaConsequencia' },
          { pontodecontrole: resources.pontodecontrole }
        )}
        handleConfirm={() => handleConfirmarExclusao(item)}
      />
    );
  };

  const handleOpenAddConsequencia = () => {
    ContentManager.addContent(
      <ConsequenciasRiscoEdit
        url="/RiscoConsequencias"
        parameters={{ riscoId: riscoId }}
        afterSave={refreshCount}
        valueField="descricao"
        saved={saved}
        {...props}
      />
    );
  };

  const renderButtonAddConsequenciaRiscoIA = () => [
    <Button
      id={'botaoMapearconsequenciasRisco'}
      key={1}
      className="ml-2"
      onClick={() => handleOpenAddConsequencia()}
      type="primary"
    >
      <LucideIcon color="#fff" icon="Plus" size={15} />
      <span className="ml-2">
        {capitalizeFirstLetter(resources.consequencias)}
      </span>
    </Button>,
  ];

  const handleAssistenteVirtualClick = () => {
    try {
      http
        .post('/RiscoConsequencias/ObterPromptMapearConsequencias', {
          idRisco: riscoId,
        })
        .then((response) => {
          const prompt = response.data?.prompt;

          const msgConsultoraVirtual = intl
            .formatMessage({ id: 'labelRiscos' })
            .replace('{riscos}', capitalizeFirstLetter(resources.riscos));

          ContentManager.addContent(
            <AssistenteVirtual
              user={user}
              prompt={prompt}
              title={msgConsultoraVirtual}
              actionsToolBar={renderButtonAddConsequenciaRiscoIA()}
              questionBold={intl.formatMessage({ id: 'vamosComecar' })}
              interactionType={TIPO_INTERACAO_SOPHIE.SUGERIR_CONSEQUENCIA_RISCO}
            />
          );
        });
    } catch (err) {
      errorHandler(err);
    }
  };

  const renderDescricao = (item, callback, configuracaoAtual) => {
    if (!item.descricao || disabled) {
      return;
    }

    const consequencias = model || [];
    const someConsequenciaHasChildren = consequencias.some(
      (consequencia) =>
        consequencia.hasChildren ||
        (consequencia.children && consequencia.children.length > 0)
    );

    return (
      <div id={'itemId' + item.id} className="pull-right">
        {item.controle ? (
          <Button
            id="deletePicture"
            className="pull-right"
            onClick={() => handleDesvincularConfirmClick(item, callback)}
            style={{ minWidth: '36px', minHeight: '36px' }}
            color={'danger'}
          >
            <LucideIcon color="#fff" icon="Trash2" size={15} />
          </Button>
        ) : (
          <a
            className="justify-content-end"
            tabIndex="-1"
            onClick={() => handleDescricaoClick(item, callback)}
            style={{
              cursor: 'pointer',
              color:
                configuracaoAtual.associacaoControleCausaConsequenciaObrigatoria &&
                !someConsequenciaHasChildren
                  ? '#e51c23'
                  : '#1d7789',
            }}
          >
            {intl.formatMessage(
              { id: 'vincularControle' },
              { pontodecontrole: resources.pontodecontrole }
            )}
            {configuracaoAtual.associacaoControleCausaConsequenciaObrigatoria &&
            !someConsequenciaHasChildren
              ? ' *'
              : ''}
          </a>
        )}
      </div>
    );
  };

  const actionsToolBar = [
    <Fragment key={1}>
      <Button
        id={'botaoMapearConsequenciasRisco'}
        className="ml-2"
        onClick={handleAssistenteVirtualClick}
        type="primary"
      >
        <SophieIcon color="#fff" size={15} />
        <span className="ml-2">
          {intl.formatMessage(
            { id: 'mapearConsequencias' },
            {
              consequencias: capitalizeFirstLetter(resources.consequencias),
            }
          )}
        </span>
      </Button>
      <UncontrolledTooltip
        placement="right"
        target={'botaoMapearConsequenciasRisco'}
      >
        {intl.formatMessage(
          {
            id: 'labelSophieToolTipMapearConsequenciasRisco',
          },
          {
            riscos: resources.riscos,
          }
        )}
      </UncontrolledTooltip>
    </Fragment>,
  ];

  return (
    <List
      editComponent={ConsequenciasRiscoEdit}
      url="/RiscoConsequencias"
      showDelete={true}
      showExportItem={false}
      showExport={false}
      hierarchy={true}
      valueField="descricao"
      saved={saved}
      showNew={!disabled}
      showMenu={!disabled}
      parameters={{ riscoId: riscoId }}
      afterSave={refreshCount}
      afterDelete={refreshCount}
      onDeleting={handleDesvincularClick}
      actionsToolBarEnd={actionsToolBar}
      hideInsertDropDown={true}
      {...props}
      resources={resources}
      moduloInteligenciaArtificial={moduloInteligenciaArtificial}
    >
      <ListColumn
        headerText={intl.formatMessage({ id: 'descricao' })}
        tree={true}
        valueField="descricao"
        required
      />
      {!disabled &&
        configuracao &&
        configuracao.associarControleCausaConsequencia && (
          <ListColumn
            valueField="addControle"
            valueFunction={(item, callback) =>
              renderDescricao(item, callback, configuracao)
            }
            required={
              configuracao.associacaoControleCausaConsequenciaObrigatoria
            }
          />
        )}
    </List>
  );
};

export default ConsequenciasRiscoList;
